import React, { useState, useEffect } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { DifferentLocation } from "../../../../../../../../models/location";
import { Warehouse } from "../../../../../../../../models/warehouse";
import { Status } from "../../../../../../../../util/enums";
import Input from "../../../../../../../../components/Form/Input";
import AddressInfoForm from "../../../../../../../../components/Generic/AddressComponent/AddressInfoForm";
import Modal from "../../../../../../../../components/Generic/Modal";
import useStyles from "./styles";

interface Props {
  warehouse?: Warehouse;
  open: boolean;
  isLoading?: boolean;
  closeModal: () => void;
  submit: (warehouse: Warehouse, oldData?: Warehouse) => void;
  view?: boolean;
  edit?: boolean;
}

const AddWarehouseModal = ({
  warehouse,
  open,
  isLoading = false,
  closeModal,
  submit,
  view,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [name, setName] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [status, setStatus] = useState<string>(Status.ACTIVE);
  const [location, setLocation] = useState<DifferentLocation>();
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;
  const schema = yup.object().shape({
    code: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (code) =>
        code ? code !== "" : false
      ),
    location: yup
      .object({
        longAddress: yup.string().required(translate("thisFieldIsRequired")),
      })
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    name: yup.string().required(translate("youMustEnterAName")),
  });

  const clearFields = () => {
    setName("");
    setLocation(undefined);
    setCode("");
    setStatus(Status.ACTIVE);
    setErrors([]);
    setHasFormBeenSubmitted(false);
  };

  const isValid = (warehouseValue: any) => {
    try {
      schema.validateSync(
        {
          code: warehouseValue?.code,
          location: warehouseValue?.location,
          name: warehouseValue?.name,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    const object = {
      code,
      location,
      name,
      status,
    };

    if (isValid(object)) {
      submit(object, warehouse);
      clearFields();
      closeModal();
    }
  };

  useEffect(() => {
    if (warehouse) {
      setCode(warehouse?.code || "");
      setLocation(warehouse?.location || undefined);
      setName(warehouse?.name || "");
      setStatus(warehouse?.status || Status.ACTIVE);
    }
  }, [warehouse]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      const object = {
        code,
        name,
        location,
      };

      isValid(object);
    }
  }, [code, name, location, hasFormBeenSubmitted]);
  const getModalName = () => {
    if (view) return translate("viewWarehouse");
    if (warehouse) return translate("editWarehouse");

    return translate("createWarehouse");
  };

  return (
    <Modal
      data-cy="warehouseModal"
      open={open}
      handleClose={() => {
        clearFields();
        closeModal();
      }}
      title={getModalName()}
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        saveDisabled: view,
        textSave: warehouse ? translate("update") : translate("create"),
        dataCySave: "submitButton",
        onSave: handleSubmit,
      }}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item xs={6} lg={6}>
            <Input
              data-cy="code"
              disabled={isLoading || view}
              name="code"
              label={translate("code")}
              placeholder={translate("code")}
              error={!!getErrorByField("code")}
              errorMessage={getErrorByField("code")}
              value={code}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} lg={6}>
            <Input
              data-cy="name"
              disabled={isLoading || view}
              name="name"
              label={translate("name")}
              placeholder={translate("name")}
              error={!!getErrorByField("name")}
              errorMessage={getErrorByField("name")}
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
          </Grid>
          <Grid item sm={12}>
            <AddressInfoForm
              hasToShowOnlyOne={true}
              isExpandedAddress={true}
              addressesArray={
                location ? ([location] as DifferentLocation[]) : ([] as DifferentLocation[])
              }
              view={view}
              condition={""}
              update={(value) => setLocation(value[0])}
              hasError={!!getErrorByField("location.longAddress")}
              errorMessage={getErrorByField("location.longAddress")}
              dataCy="address"
            />
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default AddWarehouseModal;
